import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "../../../Components/Form/FormField";
import TextareaForm from "../../../Components/Form/TextareaForm";
import Swal from "sweetalert2";
import RowLoader from "../../../Components/UI/RowLoader";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../../Components/UI/PageTitle";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchSubscriptionExpiry } from "../../../Redux/SubscriptionExpiry/SubscriptionExpiryAction";
import { fetchWalletBalance } from "../../../Redux/Auth/AuthActions";
import {
  getSubscriptionDurationsDropdownList,
  renewClientSubscription,
  showNotificationForRenew,
} from "../../../apis";
import DropdownForm from "../../../Components/Form/DropdownForm";
import Loader from "../../../Components/Loader";
import { clearSubscriptionExpiryData } from "../../../Redux/SubscriptionExpiry/SubscriptionExpirySlice";

const RenewSubscriptionForm = () => {
  const [showRenewSubscriptionForm, setShowRenewSubscriptionForm] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState("");
  const [durationsList, setDurationsList] = useState([]);
  const { walletBalance } = useSelector((state) => state.Auth);

  const { expireDate: expireDateForUpdate, renewValue } = useSelector(
    (state) => state.SubscriptionExpirySlice
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Form Validation Schema
  const validationSchema = Yup.object().shape({
    renewValue: Yup.string().required("Required"),
    expirationDate: Yup.string().required("Required"),
    duration_id: Yup.string().required("Required"),
    confirm: Yup.boolean().oneOf([true], "You must confirm your choices"),
  });

  useEffect(() => {
    setIsLoading(true);
    showNotificationForRenew()
      .then((response) => {
        setShowRenewSubscriptionForm(response.data.data.showExpireSoonAlert);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    if (selectedDuration) {
      dispatch(fetchSubscriptionExpiry(selectedDuration));
    }

    return () => {
      dispatch(clearSubscriptionExpiryData());
    };
  }, [dispatch, selectedDuration]);

  useEffect(() => {
    dispatch(fetchWalletBalance());
  }, [dispatch]);

  // get dropdown durations list
  useEffect(() => {
    getSubscriptionDurationsDropdownList()
      .then((response) => {
        setDurationsList(response.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const handleIntialValues = { ...initialValues };
    delete handleIntialValues.confirm;
    const handleValues = { ...values };
    delete handleValues.confirm;
    delete handleValues.expirationDate;
    delete handleValues.renewValue;

    setSubmitting(true);
    renewClientSubscription(handleValues)
      .then(() => {
        Swal.fire({
          title: "Success!",
          text: "Subscription renewal completed successfully.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/");
        });
        resetForm(); // Reset form after successful submission
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        // Show error alert
        Swal.fire({
          title: "Error!",
          text: "Something went wrong. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const initialValues = {
    renewValue: renewValue ?? "",
    expirationDate: expireDateForUpdate ?? "",
    duration_id: selectedDuration ?? "",
    notes: "",
    confirm: false,
  };
  if (isLoading) return <Loader />;

  if (walletBalance < renewValue)
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[60vh] tw-text-center">
        <p className="tw-text-xl tw-font-semibold tw-text-red-600 tw-mb-4">
          Subscription Renewal Failed
        </p>
        <p className="tw-text-gray-700 tw-text-base tw-max-w-md">
          Sorry, you must have at least{" "}
          <span className="tw-font-bold tw-text-red-500">
            {renewValue} E-Money
          </span>{" "}
          in your registration wallet to renew your subscription.
        </p>
      </div>
    );

  // make this falsy (!)
  if (!showRenewSubscriptionForm && !isLoading)
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[60vh] tw-text-center">
        <p className="tw-text-gray-700 tw-text-base tw-max-w-md">
          You can only renew your subscription when there are{" "}
          <span className="tw-font-bold tw-text-red-500">30 days or fewer</span>{" "}
          remaining. Please try again later.
        </p>
      </div>
    );

  return (
    <>
      <PageTitle title="Renew Subscription" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form className="card !tw-rounded-xl">
            <div className="card-body tw-shadow-xl tw-rounded-xl">
              <div className="row">
                {/* Duration */}
                <DropdownForm
                  handleChange={(e) => {
                    handleChange(e);
                    setSelectedDuration(e.target.value);
                  }}
                  name="duration_id"
                  dataToDrop={durationsList}
                  error={errors.duration_id}
                  touched={touched.duration_id}
                  label="select duration Plan "
                  placeholder="select duration Plan "
                  gridStyle="col-lg-4 col-md-12"
                  labelKey="duration_name"
                  valueKey="id"
                />

                {/* Renew Value */}
                <FormField
                  required={false}
                  name="renewValue"
                  label="Renew Value"
                  placeholder="Renew Value"
                  touched={touched}
                  errors={errors}
                  col_md="4"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.renewValue}
                  disabled
                />

                {/* Expiration Date */}
                <FormField
                  required={false}
                  disabled
                  name="expirationDate"
                  label="Expiration Date"
                  placeholder="Expiration Date"
                  touched={touched}
                  errors={errors}
                  col_md="4"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  value={values.expirationDate}
                />

                {/* Textarea for promo video Link */}
                <TextareaForm
                  required={false}
                  name="notes"
                  placeholder="Notes"
                  touched={touched.notes}
                  errors={errors.notes}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  label="Notes"
                />

                {/* confirm */}
                <div className="col-md-12 tw-flex tw-items-center">
                  <input
                    type="checkbox"
                    name="confirm"
                    id="confirm"
                    checked={values.confirm}
                    onChange={handleChange}
                    className="!tw-mr-2 tw-w-4 "
                  />
                  <label
                    htmlFor="confirm"
                    className="tw-text-sm tw-cursor-pointer !tw-m-0 !tw-mt-1"
                  >
                    I confirm that , I want to renew my subscription now{" "}
                  </label>
                </div>
                {errors.confirm && touched.confirm && (
                  <p className="tw-text-red-500 tw-text-xs tw-mt-1">
                    {errors.confirm}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="tw-mt-4 tw-text-right">
                <button
                  type="submit"
                  className="!tw-bg-primary btn tw-text-white !tw-w-[100px] hover:!tw-bg-primary/90 hover:tw-text-white"
                >
                  {isSubmitting ? (
                    <RowLoader paddingStyle="tw-py-0" />
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RenewSubscriptionForm;
